import React, { useCallback, useEffect, useState } from "react";
import MUIDataTable from "mui-datatables";
import {
  Avatar,
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { toast } from "react-toastify";
import Axios from "../axios";

const ROWS_PER_PAGE = 10;
const FULL_LIST_LIMIT = 500;

const RaceAssociationPicker = ({
  title,
  endpoint,
  typeOptions,
  columns,
  getThumbnail,
  selectedIds,
  lockedIds = [],
  onToggle,
  onRowClick,
  disabled = false,
  disabledMessage = "Deactivate this race cation to add or remove items from this selection",
  enableReorder = false,
  onReorder,
  filterVariant = "tabs",
  avatar = false,
  maxSelected,
  featuredIds,
  onToggleFeatured,
  maxFeatured,
  featuredLabel = "Featured",
}) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND;
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [loading, setLoading] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor));

  const showError = (err) => {
    toast.error(err?.response?.data?.message || err?.response?.data?.error || "Something went wrong");
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(0);
      setSearch(searchInput);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  const fetchRows = useCallback(() => {
    setLoading(true);
    Axios.get(`${BACKEND_URL}${endpoint}`, {
      params: {
        page: enableReorder ? 1 : page + 1,
        limit: enableReorder ? FULL_LIST_LIMIT : ROWS_PER_PAGE,
        search: search || undefined,
        type: typeOptions && selectedType !== "all" ? selectedType : undefined,
      },
    })
      .then((res) => {
        setRows(res?.data?.data?.docs || []);
        setTotalCount(res?.data?.data?.totalDocs || 0);
      })
      .catch(showError)
      .finally(() => setLoading(false));
  }, [BACKEND_URL, endpoint, page, search, selectedType, typeOptions, enableReorder]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // Selection state is folded into each row's own data (rather than read from the
  // `selectedIds` closure alone) so the table's underlying `data` actually changes
  // whenever selection changes, guaranteeing customBodyRenderLite re-renders the
  // checkbox column instead of it going stale.
  const tableData = rows.map((record) => ({
    ...record,
    __isSelected: selectedIds.includes(record._id),
    __isLocked: lockedIds.includes(record._id),
    __isFeatured: Boolean(featuredIds?.includes(record._id)),
  }));

  // Checked items are pulled to the top, in the exact sequence they're stored in —
  // this IS the display/website order, so putting them first is what lets an admin
  // manage the sequence without hunting across pages for checked rows.
  const orderedTableData = enableReorder
    ? [
        ...selectedIds.map((id) => tableData.find((record) => record._id === id)).filter(Boolean),
        ...tableData.filter((record) => !record.__isSelected),
      ]
    : tableData;

  const renderThumbnail = (record) => {
    if (!getThumbnail) return null;
    const thumb = getThumbnail(record);
    if (avatar) {
      return (
        <Avatar variant="rounded" src={thumb?.src || undefined} alt={thumb?.alt || ""} sx={{ width: 44, height: 44, bgcolor: "#e0e0e0" }}>
          {!thumb?.src ? (thumb?.alt || "?").trim().charAt(0).toUpperCase() : null}
        </Avatar>
      );
    }
    return thumb ? (
      <img
        src={thumb.src}
        alt={thumb.alt || ""}
        style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 6, border: "1px solid #ddd", backgroundColor: "#f7f7f7" }}
      />
    ) : (
      <Box sx={{ width: 44, height: 44, borderRadius: 1, bgcolor: "#eee" }} />
    );
  };

  const maxReached = Boolean(maxSelected) && selectedIds.length >= maxSelected && !disabled;

  const renderCheckbox = (record) => {
    const limitBlocksThis = maxReached && !record.__isSelected;
    const checkbox = (
      <Checkbox
        checked={record.__isSelected}
        disabled={disabled || record.__isLocked || limitBlocksThis}
        onClick={(e) => e.stopPropagation()}
        onChange={() => onToggle(record._id, record)}
      />
    );
    if (disabled) {
      return (
        <Tooltip title={disabledMessage}>
          <span>{checkbox}</span>
        </Tooltip>
      );
    }
    if (record.__isLocked) {
      return (
        <Tooltip title="Already added to this race cation and cannot be removed">
          <span>{checkbox}</span>
        </Tooltip>
      );
    }
    if (limitBlocksThis) {
      return (
        <Tooltip title={`You can feature at most ${maxSelected} — remove one to add another`}>
          <span>{checkbox}</span>
        </Tooltip>
      );
    }
    return checkbox;
  };

  // Featured is a highlighted subset of whatever's already selected — it can
  // only be set on rows that are selected, and only up to maxFeatured.
  const featuredMaxReached = Boolean(maxFeatured) && (featuredIds?.length || 0) >= maxFeatured && !disabled;

  const renderFeaturedCheckbox = (record) => {
    if (!onToggleFeatured) return null;
    const limitBlocksThis = featuredMaxReached && !record.__isFeatured;
    const checkbox = (
      <Checkbox
        checked={record.__isFeatured}
        disabled={disabled || !record.__isSelected || limitBlocksThis}
        onClick={(e) => e.stopPropagation()}
        onChange={() => onToggleFeatured(record._id, record)}
      />
    );
    if (disabled) {
      return (
        <Tooltip title={disabledMessage}>
          <span>{checkbox}</span>
        </Tooltip>
      );
    }
    if (!record.__isSelected) {
      return (
        <Tooltip title="Select this item first to feature it">
          <span>{checkbox}</span>
        </Tooltip>
      );
    }
    if (limitBlocksThis) {
      return (
        <Tooltip title={`You can feature at most ${maxFeatured} — remove one to add another`}>
          <span>{checkbox}</span>
        </Tooltip>
      );
    }
    return checkbox;
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = selectedIds.indexOf(active.id);
    const newIndex = selectedIds.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(arrayMove(selectedIds, oldIndex, newIndex));
  };

  const SortableAssociationRow = ({ record }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: record._id });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.6 : 1,
      backgroundColor: isDragging ? "#f5f5f5" : "rgba(25, 118, 210, 0.04)",
    };
    return (
      <TableRow ref={setNodeRef} style={style} hover sx={{ cursor: "pointer" }} onClick={() => onRowClick(record)}>
        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
          <span {...attributes} {...listeners} style={{ cursor: "grab", display: "inline-flex" }}>
            <DragIndicatorIcon style={{ color: "#888" }} />
          </span>
        </TableCell>
        <TableCell onClick={(e) => e.stopPropagation()}>{renderCheckbox(record)}</TableCell>
        {onToggleFeatured && <TableCell onClick={(e) => e.stopPropagation()}>{renderFeaturedCheckbox(record)}</TableCell>}
        {getThumbnail && <TableCell>{renderThumbnail(record)}</TableCell>}
        {columns.map((column) => (
          <TableCell key={column.name}>{column.render(record)}</TableCell>
        ))}
      </TableRow>
    );
  };

  const StaticAssociationRow = ({ record }) => (
    <TableRow hover sx={{ cursor: "pointer" }} onClick={() => onRowClick(record)}>
      <TableCell />
      <TableCell onClick={(e) => e.stopPropagation()}>{renderCheckbox(record)}</TableCell>
      {onToggleFeatured && <TableCell onClick={(e) => e.stopPropagation()}>{renderFeaturedCheckbox(record)}</TableCell>}
      {getThumbnail && <TableCell>{renderThumbnail(record)}</TableCell>}
      {columns.map((column) => (
        <TableCell key={column.name}>{column.render(record)}</TableCell>
      ))}
    </TableRow>
  );

  const columnCount = 2 + (onToggleFeatured ? 1 : 0) + (getThumbnail ? 1 : 0) + columns.length;

  const tableColumns = [
    {
      name: "__select",
      label: "Select",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (i) => {
          const record = tableData[i];
          return record ? renderCheckbox(record) : null;
        },
      },
    },
    onToggleFeatured && {
      name: "__featured",
      label: featuredLabel,
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (i) => {
          const record = tableData[i];
          return record ? renderFeaturedCheckbox(record) : null;
        },
      },
    },
    getThumbnail && {
      name: "__thumbnail",
      label: "",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (i) => {
          const record = tableData[i];
          return record ? renderThumbnail(record) : null;
        },
      },
    },
    ...columns.map((column) => ({
      name: column.name,
      label: column.label,
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (i) => {
          const record = tableData[i];
          return record ? column.render(record) : "-";
        },
      },
    })),
  ].filter(Boolean);

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        {title} ({selectedIds.length}{maxSelected ? ` / ${maxSelected}` : ""} selected)
        {onToggleFeatured ? `, ${featuredIds?.length || 0}${maxFeatured ? ` / ${maxFeatured}` : ""} ${featuredLabel.toLowerCase()}` : ""}
        {disabled ? (enableReorder ? " — Locked for add/remove (race cation is active); drag-to-reorder is still available" : " — Locked (race cation is active)") : ""}
      </Typography>

      {typeOptions &&
        (filterVariant === "dropdown" ? (
          <FormControl size="small" sx={{ minWidth: 220, mb: 1.5 }}>
            <InputLabel id={`${title}-type-filter-label`}>Filter by type</InputLabel>
            <Select
              labelId={`${title}-type-filter-label`}
              label="Filter by type"
              value={selectedType}
              onChange={(e) => {
                setPage(0);
                setSelectedType(e.target.value);
              }}
            >
              <MenuItem value="all">All</MenuItem>
              {typeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 1.5 }}>
            <Tabs
              value={selectedType}
              onChange={(_, val) => {
                setPage(0);
                setSelectedType(val);
              }}
            >
              <Tab label="All" value="all" />
              {typeOptions.map((option) => (
                <Tab key={option.value} label={option.label} value={option.value} />
              ))}
            </Tabs>
          </Box>
        ))}

      {enableReorder ? (
        <>
          <TextField
            size="small"
            placeholder="Search..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ mb: 1.5, minWidth: 260 }}
          />
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} modifiers={[restrictToVerticalAxis]}>
            <SortableContext items={selectedIds} strategy={verticalListSortingStrategy}>
              <Box sx={{ maxHeight: 480, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                <TableContainer>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width={40} />
                        <TableCell width={56}>Select</TableCell>
                        {onToggleFeatured && <TableCell width={72}>{featuredLabel}</TableCell>}
                        {getThumbnail && <TableCell width={64} />}
                        {columns.map((column) => (
                          <TableCell key={column.name}>{column.label}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orderedTableData.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={columnCount}>{loading ? "Loading..." : "No records found"}</TableCell>
                        </TableRow>
                      )}
                      {selectedIds.length > 0 && orderedTableData[0]?.__isSelected && (
                        <TableRow>
                          <TableCell colSpan={columnCount} sx={{ bgcolor: "action.hover", py: 0.5, border: 0 }}>
                            <Typography variant="caption" color="text.secondary">
                              Selected — drag the handle to set the sequence shown on the website
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {orderedTableData.map((record, index) => {
                        const row = record.__isSelected ? (
                          <SortableAssociationRow key={record._id} record={record} />
                        ) : (
                          <StaticAssociationRow key={record._id} record={record} />
                        );
                        const isFirstUnselected = !record.__isSelected && orderedTableData[index - 1]?.__isSelected;
                        if (isFirstUnselected) {
                          return (
                            <React.Fragment key={`group-${record._id}`}>
                              <TableRow>
                                <TableCell colSpan={columnCount} sx={{ bgcolor: "action.hover", py: 0.5, border: 0 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Available — check to add
                                  </Typography>
                                </TableCell>
                              </TableRow>
                              {row}
                            </React.Fragment>
                          );
                        }
                        return row;
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </SortableContext>
          </DndContext>
        </>
      ) : (
        <MUIDataTable
          title=""
          data={tableData}
          columns={tableColumns}
          options={{
            responsive: "standard",
            selectableRows: "none",
            filter: false,
            viewColumns: false,
            download: false,
            elevation: 0,
            serverSide: true,
            search: true,
            searchText: searchInput,
            count: totalCount,
            page,
            rowsPerPage: ROWS_PER_PAGE,
            rowsPerPageOptions: [],
            textLabels: { body: { noMatch: loading ? "Loading..." : "No records found" } },
            onRowClick: (_rowData, rowMeta) => {
              const record = tableData[rowMeta.dataIndex];
              if (record) onRowClick(record);
            },
            setRowProps: () => ({ style: { cursor: "pointer" } }),
            onTableChange: (action, tableState) => {
              if (action === "changePage") {
                setPage(tableState.page);
              }
            },
            onSearchChange: (searchQuery) => {
              setSearchInput(searchQuery || "");
            },
            onSearchClose: () => {
              setSearchInput("");
            },
          }}
        />
      )}
    </Box>
  );
};

export default RaceAssociationPicker;
